import { Link } from "react-router-dom";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { ArrowRight, ShieldCheck } from "lucide-react";

const PRINCIPLES = [
  { n: "01", title: "Outcomes, not deliverables", desc: "We measure ourselves on the business change we create, leads booked, jobs won, hours given back, not on documents shipped." },
  { n: "02", title: "Operators, not agency hires", desc: "Senior people who&rsquo;ve built and run service businesses. Not juniors learning on your dime." },
  { n: "03", title: "One throat to choke", desc: "One team. One contract. One accountable point of contact. We integrate the stack so you don&rsquo;t have to." },
  { n: "04", title: "Honest fit calls", desc: "About a third of audits end with us saying 'not us'. We&rsquo;d rather lose the build than take on a client we can&rsquo;t serve." },
  { n: "05", title: "Own your stack", desc: "Your domain, your website, your CRM, your data, always yours. If you ever leave, the system keeps running. No lock-in tricks." },
  { n: "06", title: "Discreet by default", desc: "Most clients prefer to keep their playbook off public record. Confidentiality is the baseline, not the upsell." },
];

export default function About() {
  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="about-hero">
        <div className="overline">About Toulios Advisory Group</div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          A senior advisory firm built to advise, build, and run.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          Toulios Advisory Group is the trading name of Toulios Wealth
          Management Pty Ltd (ACN 667 133 977 · ABN 68 667 133 977), an
          Australian management consulting firm. We help established
          operators grow across the four levers that move the number:
          financials, sales, operations and systems.
        </p>
      </section>

      <section className="section-y" data-testid="about-story">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Our story"
              title="Built from the operator&rsquo;s side of the desk."
              description="Toulios exists because too many great trade businesses are still running on grit, mobiles and Friday-night admin. We close that gap."
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-zinc-300 leading-relaxed">
            <p>
              The firm is classified under ANZSIC code{" "}
              <span className="text-white">M696299, Management Advice and Related Consulting Services (n.e.c.)</span>{" "}
              and is structured as four divisions, each addressing one growth
              lever:
            </p>
            <ul className="space-y-2 text-zinc-300">
              <li><span className="text-gold">01 · </span> <span className="text-white">Toulios Growth Systems</span>, websites, lead capture, CRM, dashboards, AI, outsourced execution.</li>
              <li><span className="text-gold">02 · </span> <span className="text-white">Toulios Financial Strategy</span>, cashflow, structuring, margin, capital.</li>
              <li><span className="text-gold">03 · </span> <span className="text-white">Toulios Revenue & Sales</span>, pricing, sales process, conversion, pipeline.</li>
              <li><span className="text-gold">04 · </span> <span className="text-white">Toulios Operations</span>, SOPs, outsourcing, admin systems, efficiency.</li>
            </ul>
            <p>
              Most clients enter through our flagship product,{" "}
              <span className="text-white">The Toulios Growth System</span>,
              and expand into the other divisions over time as a long-term
              retained relationship.
            </p>
            <p>
              We don&rsquo;t scale by volume. A small number of operators per
              quarter. Senior people, in the room, on every engagement.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== TEAM ===================== */}
      <section className="section-y border-t border-white/[0.05]" data-testid="about-team">
        <div className="container-x">
          <SectionHeading
            eyebrow="The people behind the firm"
            title="Senior. Accountable. On every engagement."
            description="Toulios is intentionally small. The people you meet on the first Strategy Call are the people who do the work."
          />

          {/* Founder */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="relative border border-gold/30 bg-[#0a0a0a] aspect-[4/5] flex items-center justify-center overflow-hidden">
                  <div
                    aria-hidden
                    className="absolute inset-0 opacity-30"
                    style={{
                      background:
                        "radial-gradient(closest-side, rgba(212,175,55,0.18), transparent 70%)",
                    }}
                  />
                  <span className="relative font-display text-7xl text-gold tracking-tight">
                    {/* TODO: replace with Dimitri's headshot when supplied */}
                    DT
                  </span>
                  <div className="absolute bottom-0 inset-x-0 border-t border-white/[0.06] p-4 bg-[#070707]">
                    <div className="font-display text-lg text-white">Dimitri Toulios</div>
                    <div className="text-xs text-gold tracking-[0.18em] uppercase mt-1">
                      Managing Director · Founder
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal delay={100}>
                <div className="overline">Founder</div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">
                  Dimitri Toulios
                </h3>
                <p className="mt-6 text-zinc-300 leading-relaxed text-[15px] max-w-2xl">
                  Dimitri founded Toulios Advisory Group to bring
                  institutional-grade financial and growth rigour to the
                  operators who rarely get it. His career sits at the
                  intersection of small business banking and operator-side
                  advisory, working directly with established Australian SMEs
                  on credit structuring, cashflow strategy and growth-stage
                  capital. The firm was built around a simple observation:
                  too many great trade, service and industrial businesses
                  are doing real revenue but running on grit and
                  Friday-night admin. Toulios closes that gap.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl">
                  {[
                    { k: "Current focus", v: "Founder, Toulios Advisory Group" },
                    { k: "Background", v: "Small business banking · SME advisory" },
                    { k: "Based", v: "Melbourne · Servicing nationally" },
                    { k: "Linked", v: "linkedin.com/in/dimitritoulios" },
                  ].map((m) => (
                    <div key={m.k} className="border-l border-gold/30 pl-3">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">{m.k}</div>
                      <div className="mt-1 text-sm text-zinc-300">{m.v}</div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>

          {/* Advisory board */}
          <div className="mt-24">
            <Reveal>
              <div className="overline">Advisory board</div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">
                Senior operators, in the room when we need them.
              </h3>
              <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed text-[15px]">
                Independent advisors sit alongside the firm. They sharpen our
                thinking on strategy, structure and capital, and review every
                retained engagement on a quarterly cadence.
              </p>
            </Reveal>

            <Reveal delay={100}>
              <div
                className="mt-12 border border-gold/30 bg-[#0a0a0a] p-8 md:p-10"
                data-testid="advisory-board-card"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-2 flex justify-start">
                    <div className="relative w-20 h-20 border border-gold/30 bg-[#070707] flex items-center justify-center">
                      <div
                        aria-hidden
                        className="absolute inset-0 opacity-40"
                        style={{
                          background:
                            "radial-gradient(closest-side, rgba(212,175,55,0.22), transparent 70%)",
                        }}
                      />
                      <ShieldCheck className="relative w-7 h-7 text-gold" />
                    </div>
                  </div>
                  <div className="lg:col-span-7">
                    <div className="overline">Composition</div>
                    <p className="mt-3 text-zinc-300 leading-relaxed text-[15px]">
                      Two independent advisors with backgrounds in
                      institutional investment, portfolio strategy and SME
                      operations. They join quarterly engagement reviews and
                      are available on call for material strategy or capital
                      decisions.
                    </p>
                    <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
                      Individual advisor identities are disclosed during the
                      Strategy Call or on engagement, with each advisor&rsquo;s
                      written consent. We do not publish advisor names
                      publicly.
                    </p>
                  </div>
                  <div className="lg:col-span-3 lg:text-right">
                    <Link to="/contact#advisor" className="btn-outline-white" data-testid="advisory-board-cta">
                      Apply to advise <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Credentials strip */}
          <div className="mt-20 border border-white/[0.06] bg-[#070707] p-8 md:p-10" data-testid="about-credentials">
            <div className="overline">Credentials &amp; structure</div>
            <h3 className="mt-4 font-display text-xl md:text-2xl tracking-tight">
              The legal and professional footing.
            </h3>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { k: "Legal entity", v: "Toulios Wealth Management Pty Ltd" },
                { k: "ACN · ABN", v: "ACN 667 133 977 · ABN 68 667 133 977" },
                { k: "ANZSIC", v: "M696299 · Management Advice & Related Consulting" },
                { k: "Privacy", v: "APP-compliant · See Trust & Privacy" },
              ].map((m) => (
                <div key={m.k} className="border-l border-gold/30 pl-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">{m.k}</div>
                  <div className="mt-2 text-sm text-zinc-200 leading-relaxed">{m.v}</div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[11px] text-zinc-500 leading-relaxed max-w-2xl">
              Toulios Advisory Group provides general management advisory only.
              We do not hold an Australian Financial Services Licence. Nothing
              we publish constitutes personal financial product advice. Read
              the full <Link to="/legal/disclosures" className="text-zinc-300 hover:text-gold underline-offset-2 hover:underline">Disclosures</Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="about-principles">
        <div className="container-x">
          <SectionHeading
            eyebrow="Principles"
            title="Six rules we don&rsquo;t break."
            description="The boundaries that make the work good."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.05]">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 60} className="bg-ink p-8 md:p-10">
                <div className="overline">{p.n}</div>
                <h3 className="mt-6 text-xl md:text-2xl font-display font-light tracking-tight">{p.title}</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y" data-testid="about-cta">
        <div className="container-x">
          <div className="surface p-10 md:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="overline">Work with us</div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight max-w-2xl">
                Start with a 60-minute audit. We&rsquo;ll tell you straight whether we&rsquo;re the right partner.
              </h3>
            </div>
            <Link to="/contact" className="btn-gold" data-testid="about-cta-book">
              Book a Strategy Call <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
